import { Autocomplete, Box, Button, TextField } from "@mui/material";
import { useMemo, useEffect, useState } from "react";
import { ADMISSION_STATUS_FILTER_OPTIONS, UNLINKED_UNIVERSITY } from "./admissionListFilters";

export const EMPTY_ADMISSION_FILTERS = {
  caseOfficer: "",
  course: null,
  university: null,
  intake: "",
  status: "",
  accountVerificationStatus: "",
  search: "",
  pageIndex: 0,
  pageSize: 20,
};

const normalizeFilterValue = (value) => (value == null ? "" : String(value));

const ACCOUNT_STATUS_OPTIONS = ["PENDING", "VERIFIED", "IMPORTED", "REJECTED"];

const accountStatusLabel = (o) =>
  o === "PENDING"
    ? "Pending Verification"
    : o === "VERIFIED"
    ? "Verified"
    : o === "IMPORTED"
    ? "Imported"
    : o === "REJECTED"
    ? "Rejected"
    : o;

const UNLINKED_UNIVERSITY_OPTION = { id: UNLINKED_UNIVERSITY, name: "Unlinked" };

const fieldSx = {
  minWidth: 160,
  flex: "1 1 160px",
  maxWidth: 220,
};

const FilterField = ({ children }) => (
  <Box sx={{ ...fieldSx, display: "flex", alignItems: "flex-end" }}>{children}</Box>
);

const AdmissionFiltersPanel = ({ filters, setFilters, uniData = [], mode = "clients" }) => {
  const showClientDiscovery = mode === "clients";
  const showAdmissionDiscovery = mode === "admissions";
  const showAdmissionStatus = mode === "admissions" || mode === "clients";

  const [caseOfficerInput, setCaseOfficerInput] = useState(filters.caseOfficer || "");
  useEffect(() => {
    setCaseOfficerInput(filters.caseOfficer || "");
  }, [filters.caseOfficer]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (caseOfficerInput !== (filters.caseOfficer || "")) {
        setFilters((f) => ({ ...f, caseOfficer: caseOfficerInput }));
      }
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseOfficerInput]);

  const universityOptions = useMemo(
    () => [UNLINKED_UNIVERSITY_OPTION, ...(uniData || [])],
    [uniData]
  );

  const hasActive = Boolean(
    filters.university ||
      filters.course ||
      filters.intake ||
      filters.status ||
      filters.caseOfficer ||
      filters.accountVerificationStatus ||
      filters.search
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {(showClientDiscovery || showAdmissionDiscovery) && (
          <FilterField>
            <Autocomplete
              fullWidth
              size="small"
              options={universityOptions}
              getOptionLabel={(o) => o?.name || o?.universityName || ""}
              value={
                universityOptions.find(
                  (u) => normalizeFilterValue(u.id) === normalizeFilterValue(filters.university)
                ) || null
              }
              onChange={(_, v) =>
                setFilters((f) => ({
                  ...f,
                  university: v?.id == null ? "" : v.id,
                }))
              }
              isOptionEqualToValue={(o, v) =>
                normalizeFilterValue(o?.id) === normalizeFilterValue(v?.id)
              }
              renderInput={(params) => <TextField {...params} label="Uni/College" size="small" />}
              ListboxProps={{ style: { maxHeight: 240 } }}
            />
          </FilterField>
        )}

        {showClientDiscovery && (
          <FilterField>
            <TextField
              fullWidth
              size="small"
              label="Case Officer"
              value={caseOfficerInput}
              onChange={(e) => setCaseOfficerInput(e.target.value)}
            />
          </FilterField>
        )}

        {showAdmissionStatus && (
          <>
            <FilterField>
              <Autocomplete
                fullWidth
                size="small"
                options={ADMISSION_STATUS_FILTER_OPTIONS}
                value={filters.status || null}
                onChange={(_, v) => setFilters((f) => ({ ...f, status: v || "" }))}
                renderInput={(params) => (
                  <TextField {...params} label="Admission status" size="small" />
                )}
              />
            </FilterField>
            <FilterField>
              <Autocomplete
                fullWidth
                size="small"
                options={ACCOUNT_STATUS_OPTIONS}
                getOptionLabel={accountStatusLabel}
                value={filters.accountVerificationStatus || null}
                onChange={(_, v) =>
                  setFilters((f) => ({ ...f, accountVerificationStatus: v || "" }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Account status" size="small" />
                )}
              />
            </FilterField>
          </>
        )}

        {hasActive && (
          <Box sx={{ flex: "0 0 auto", pb: 0.25 }}>
            <Button
              color="error"
              variant="outlined"
              onClick={() => setFilters({ ...EMPTY_ADMISSION_FILTERS })}
            >
              Clear filters
            </Button>
          </Box>
        )}
      </div>
    </div>
  );
};

export default AdmissionFiltersPanel;
